import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { Marker, Popup, Circle, useMap, CircleMarker } from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-cluster'
import { MapContainer } from './MapContainer'
import { MapFollowController } from './MapFollowController'
import { LocationCenterButton } from './LocationCenterButton'
import { AdventureDto } from '@api/types/adventure'
import { Typography, Box } from '@mui/material'
import { MyLocation } from '@mui/icons-material'
import { useGeolocation, Location } from '@hooks/useGeolocation'
import { navigateTo } from '@utils/navigation'
import { liveLocationApi, FriendLiveLocationDto } from '@api/endpoints/liveLocation'
import L from 'leaflet'

// Create a custom icon for user GPS location
const userLocationIcon = new L.DivIcon({
  className: 'user-location-marker',
  html: `
    <div style="
      width: 24px;
      height: 24px;
      background: #2196F3;
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      position: relative;
    ">
      <div style="
        width: 8px;
        height: 8px;
        background: white;
        border-radius: 50%;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      "></div>
    </div>
  `,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
})

// Create a custom icon for home/default location
const homeLocationIcon = new L.DivIcon({
  className: 'home-location-marker',
  html: `
    <div style="
      width: 24px;
      height: 24px;
      background: #FF9800;
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      position: relative;
    ">
      <div style="
        width: 8px;
        height: 8px;
        background: white;
        border-radius: 50%;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      "></div>
    </div>
  `,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
})

// Adventure type emoji mapping based on mainCategory
const ADVENTURE_TYPE_EMOJIS: Record<number, string> = {
  0: '📦', // Traditional GeoCaches (default)
  1: '🌲', // Nature Adventure
  2: '🏛️', // Historical Adventure
  3: '🏙️', // Urban Exploration
  4: '🧩', // Riddle Quest
  5: '🛍️', // Supporting Business
}

// Create a custom icon for adventure markers (with locked, subscription, type, and test version support)
const createAdventureMarkerIcon = (isLocked: boolean, requiresSubscription: boolean, mainCategory?: number, hasTestVersion?: boolean) => {
  const showLock = isLocked || requiresSubscription

  // Gear badge HTML for test version adventures
  const gearBadge = hasTestVersion ? `
    <div style="
      position: absolute;
      top: -4px;
      right: -8px;
      width: 16px;
      height: 16px;
      background: #1976d2;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3);
      border: 1.5px solid white;
    ">
      <svg width="10" height="10" viewBox="0 0 24 24" fill="white">
        <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>
      </svg>
    </div>
  ` : ''

  // If locked or requires subscription, show just the padlock emoji
  if (showLock) {
    // 85% opacity for subscription-required, 50% for just locked
    const opacity = requiresSubscription ? 0.85 : 0.5
    return new L.DivIcon({
      className: requiresSubscription ? 'adventure-marker-subscription' : 'adventure-marker-locked',
      html: `
        <div style="
          font-size: 28px;
          line-height: 1;
          opacity: ${opacity};
          text-shadow: 0 2px 4px rgba(0,0,0,0.3);
          position: relative;
        ">🔒${gearBadge}</div>
      `,
      iconSize: [28, 28],
      iconAnchor: [14, 14],
      popupAnchor: [0, -14],
    })
  }

  // Get the emoji for this adventure type (default to 📦 for Traditional GeoCaches)
  const emoji = ADVENTURE_TYPE_EMOJIS[mainCategory ?? 0] || '📦'

  // Normal marker - emoji based on adventure type
  return new L.DivIcon({
    className: 'adventure-marker',
    html: `
      <div style="
        font-size: 28px;
        line-height: 1;
        text-shadow: 0 2px 4px rgba(0,0,0,0.3);
        position: relative;
      ">${emoji}${gearBadge}</div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  })
}

// Create a custom icon for friend locations
const createFriendLocationIcon = (name: string | undefined, imageUrl?: string) => {
  // Generate initials: first letter + first letter after last space
  const getInitials = (displayName: string | undefined): string => {
    if (!displayName) return '?'
    const trimmed = displayName.trim()
    if (!trimmed) return '?'

    const lastSpaceIndex = trimmed.lastIndexOf(' ')
    if (lastSpaceIndex !== -1 && lastSpaceIndex < trimmed.length - 1) {
      // Has a space - use first letter and letter after last space
      return (trimmed.charAt(0) + trimmed.charAt(lastSpaceIndex + 1)).toUpperCase()
    }
    // No space - just use first letter
    return trimmed.charAt(0).toUpperCase()
  }

  const initials = getInitials(name)

  return new L.DivIcon({
    className: 'friend-location-marker',
    html: `
      <div style="
        width: 32px;
        height: 32px;
        background: #9C27B0;
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
      ">
        ${imageUrl
          ? `<img src="${imageUrl}" style="width: 100%; height: 100%; object-fit: cover; border-radius: 50%;" />`
          : `<span style="color: white; font-weight: bold; font-size: ${initials.length > 1 ? '10' : '12'}px;">${initials}</span>`
        }
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
  })
}

// Create combined cluster icon for both adventures and friends
const createCombinedClusterIcon = (cluster: any) => {
  const markers = cluster.getAllChildMarkers()
  let adventureCount = 0
  let friendCount = 0

  markers.forEach((marker: any) => {
    if (marker.options.markerType === 'friend') {
      friendCount++
    } else {
      adventureCount++
    }
  })

  const total = adventureCount + friendCount
  const size = total < 10 ? 28 : total < 100 ? 32 : 36

  // If only one type, show simple circle
  if (friendCount === 0) {
    return new L.DivIcon({
      html: `
        <div style="
          width: ${size}px;
          height: ${size}px;
          background: #1976d2;
          border: 2px solid white;
          border-radius: 50%;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <span style="color: white; font-weight: bold; font-size: ${total < 10 ? 11 : 10}px;">${adventureCount}</span>
        </div>
      `,
      className: 'cluster-icon',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    })
  }

  if (adventureCount === 0) {
    return new L.DivIcon({
      html: `
        <div style="
          width: ${size}px;
          height: ${size}px;
          background: #9C27B0;
          border: 2px solid white;
          border-radius: 50%;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <span style="color: white; font-weight: bold; font-size: ${total < 10 ? 11 : 10}px;">${friendCount}</span>
        </div>
      `,
      className: 'cluster-icon',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    })
  }

  // Split circle for both types
  return new L.DivIcon({
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        overflow: hidden;
        display: flex;
        border: 2px solid white;
      ">
        <div style="
          width: 50%;
          height: 100%;
          background: #1976d2;
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <span style="color: white; font-weight: bold; font-size: ${size < 32 ? 9 : 10}px;">${adventureCount}</span>
        </div>
        <div style="
          width: 50%;
          height: 100%;
          background: #9C27B0;
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <span style="color: white; font-weight: bold; font-size: ${size < 32 ? 9 : 10}px;">${friendCount}</span>
        </div>
      </div>
    `,
    className: 'combined-cluster-icon',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  })
}

interface AdventureMapProps {
  adventures: AdventureDto[]
  center?: [number, number]
  zoom?: number
  height?: string | number
  onMarkerClick?: (adventure: AdventureDto) => void
  enableAutoFollow?: boolean
  onViewportChange?: (bounds: { northEast: { lat: number; lng: number }; southWest: { lat: number; lng: number } }) => void
  showFriendLocations?: boolean
  friendLocationRefreshInterval?: number // in milliseconds, default 30000
  key?: string
}

// Helper to format duration from API timespan string for popup
function formatPopupDuration(durationStr: string | null | undefined): string {
  if (!durationStr) return ''

  // Parse format: [d.]hh:mm:ss[.fffffff]
  const dayMatch = durationStr.match(/^(\d+)\.(\d{2}):(\d{2}):(\d{2})/)
  const timeMatch = durationStr.match(/^(\d{2}):(\d{2}):(\d{2})/)

  let days = 0, hours = 0, minutes = 0, seconds = 0

  if (dayMatch) {
    days = parseInt(dayMatch[1], 10)
    hours = parseInt(dayMatch[2], 10)
    minutes = parseInt(dayMatch[3], 10)
    seconds = parseInt(dayMatch[4], 10)
  } else if (timeMatch) {
    hours = parseInt(timeMatch[1], 10)
    minutes = parseInt(timeMatch[2], 10)
    seconds = parseInt(timeMatch[3], 10)
  } else {
    return ''
  }

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`
  } else if (hours > 0) {
    return `${hours}h ${minutes}m`
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`
  } else {
    return `${seconds}s`
  }
}

// Helper function to create popup HTML content
function createPopupHTML(adventure: AdventureDto): string {
  const difficultyColor =
    adventure.difficulty <= 2 ? '#4caf50' :
    adventure.difficulty <= 3 ? '#2196f3' :
    adventure.difficulty <= 4 ? '#ff9800' : '#f44336'

  const truncatedDescription = adventure.description.length > 100
    ? adventure.description.substring(0, 100) + '...'
    : adventure.description

  // Get thumbnail URL from various possible fields
  const thumbnailUrl = adventure.thumbnailImageUrl || adventure.thumbnailUrl || adventure.coverImageUrl || adventure.imageUrl

  // Check if adventure is locked
  const isLocked = adventure.meetsJoinCriteria === false
  const lockReasons = adventure.joinCriteriaFailureReasons || []

  // Check if stats are available
  const hasStats = adventure.uniquePlayersLastHour !== undefined ||
    adventure.uniquePlayersLast24Hours !== undefined ||
    adventure.uniquePlayersLastMonth !== undefined ||
    adventure.uniquePlayersLastYear !== undefined

  // Check for session completion data (from History tab - now grouped)
  const sessionCompletedAt = (adventure as any).sessionCompletedAt as string | undefined
  const sessionDuration = (adventure as any).sessionDuration as string | undefined
  const sessionCompletionCount = (adventure as any).sessionCompletionCount as number | undefined
  const sessionAverageDuration = (adventure as any).sessionAverageDuration as string | undefined
  const hasSessionData = sessionCompletedAt || sessionDuration

  // Format duration for popup display (sessionDuration is already formatted from grouped data)
  const formattedDuration = sessionDuration || ''
  // Append Z to ensure UTC parsing, then convert to local date
  const formattedDate = sessionCompletedAt
    ? new Date(sessionCompletedAt.endsWith('Z') ? sessionCompletedAt : sessionCompletedAt + 'Z').toLocaleDateString()
    : ''
  const formattedAvgDuration = sessionAverageDuration || ''

  return `
    <div style="padding: 8px; min-width: 250px;">
      ${thumbnailUrl ? `
        <div style="margin: -8px -8px 8px -8px; border-radius: 4px 4px 0 0; overflow: hidden;">
          <img
            src="${thumbnailUrl}"
            alt="${adventure.title}"
            style="width: 100%; height: 120px; object-fit: cover; display: block;"
          />
        </div>
      ` : ''}
      <div style="display: flex; justify-content: space-between; align-items: start; margin-bottom: 8px;">
        <h3 style="margin: 0; flex: 1; font-size: 1.25rem; font-weight: 500;">${adventure.title}</h3>
      </div>
      <div style="margin-bottom: 8px;">
        <span style="
          display: inline-block;
          padding: 4px 8px;
          border-radius: 16px;
          font-size: 0.75rem;
          font-weight: 500;
          background-color: ${difficultyColor};
          color: white;
        ">Difficulty ${adventure.difficulty}/5</span>
      </div>
      ${isLocked && lockReasons.length > 0 ? `
        <div style="
          margin-bottom: 12px;
          padding: 8px;
          background-color: #fff3e0;
          border-radius: 4px;
          border-left: 3px solid #ff9800;
        ">
          <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 4px;">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#e65100">
              <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM9 8V6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9z"/>
            </svg>
            <span style="font-weight: 500; font-size: 0.8rem; color: #e65100;">Currently Unavailable</span>
          </div>
          ${lockReasons.map(reason => `
            <div style="font-size: 0.75rem; color: #666; margin-left: 22px;">${reason}</div>
          `).join('')}
        </div>
      ` : ''}
      <p style="
        margin: 0 0 12px 0;
        font-size: 0.875rem;
        color: #666;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      ">${truncatedDescription}</p>
      ${hasStats ? `
        <div style="
          margin-bottom: 12px;
          padding: 8px;
          background-color: #f5f5f5;
          border-radius: 4px;
        ">
          <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 6px;">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#666">
              <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/>
            </svg>
            <span style="font-weight: 500; font-size: 0.8rem; color: #666;">Popularity</span>
          </div>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px; font-size: 0.75rem;">
            <div style="color: #888;">Last Hour:</div>
            <div style="text-align: right; font-weight: 500;">${(adventure.uniquePlayersLastHour ?? 0).toLocaleString()}</div>
            <div style="color: #888;">Last 24h:</div>
            <div style="text-align: right; font-weight: 500;">${(adventure.uniquePlayersLast24Hours ?? 0).toLocaleString()}</div>
            <div style="color: #888;">Last Month:</div>
            <div style="text-align: right; font-weight: 500;">${(adventure.uniquePlayersLastMonth ?? 0).toLocaleString()}</div>
            <div style="color: #888;">Last Year:</div>
            <div style="text-align: right; font-weight: 500;">${(adventure.uniquePlayersLastYear ?? 0).toLocaleString()}</div>
          </div>
        </div>
      ` : ''}
      ${hasSessionData ? `
        <div style="
          margin-bottom: 12px;
          padding: 8px;
          background-color: #e8f5e9;
          border-radius: 4px;
          border-left: 3px solid #4caf50;
        ">
          <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 6px;">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#2e7d32">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
            <span style="font-weight: 500; font-size: 0.8rem; color: #2e7d32;">
              ${sessionCompletionCount && sessionCompletionCount > 1 ? `Completed ${sessionCompletionCount}x` : 'Completed'}
            </span>
          </div>
          <div style="display: grid; grid-template-columns: auto 1fr; gap: 4px 8px; font-size: 0.75rem;">
            ${formattedDate ? `
              <div style="color: #666;">Last:</div>
              <div style="font-weight: 500;">${formattedDate}</div>
            ` : ''}
            ${formattedDuration ? `
              <div style="color: #666;">Best:</div>
              <div style="font-weight: 500;">${formattedDuration}</div>
            ` : ''}
            ${sessionCompletionCount && sessionCompletionCount > 1 && formattedAvgDuration ? `
              <div style="color: #666;">Avg:</div>
              <div style="font-weight: 500;">${formattedAvgDuration}</div>
            ` : ''}
          </div>
        </div>
      ` : ''}
      <a
        href="/adventures/${adventure.id}"
        style="
          display: block;
          width: 100%;
          padding: 8px 16px;
          background-color: #1976d2;
          color: white;
          text-align: center;
          text-decoration: none;
          border-radius: 4px;
          font-weight: 500;
          font-size: 0.875rem;
        "
        onclick="event.preventDefault(); window.location.href='/adventures/${adventure.id}';"
      >View Details</a>
    </div>
  `
}

// Component to manage combined adventure and friend markers with clustering
const CombinedMarkers = React.memo(function CombinedMarkers({
  adventures,
  friends,
  onMarkerClick,
}: {
  adventures: AdventureDto[]
  friends: FriendLiveLocationDto[]
  onMarkerClick?: (adventure: AdventureDto) => void
}) {
  // Filter adventures with valid locations
  const validAdventures = useMemo(() => adventures.filter(adventure =>
    adventure.location &&
    typeof adventure.location.latitude === 'number' &&
    typeof adventure.location.longitude === 'number' &&
    !isNaN(adventure.location.latitude) &&
    !isNaN(adventure.location.longitude)
  ), [adventures])

  // Persistent cache for friend icons to prevent flashing on refresh
  const iconCacheRef = useRef<Record<string, { icon: L.DivIcon; avatarUrl: string | null }>>({})

  // Get or create icon for each friend (only recreate if avatar changed)
  const getFriendIcon = useCallback((friend: FriendLiveLocationDto) => {
    const key = friend.userId || `${friend.latitude}-${friend.longitude}`
    const cached = iconCacheRef.current[key]

    // Return cached icon if avatar hasn't changed
    if (cached && cached.avatarUrl === friend.avatarUrl) {
      return cached.icon
    }

    // Create new icon and cache it
    const friendName = friend.userName || 'Friend'
    const icon = createFriendLocationIcon(friendName, friend.avatarUrl || undefined)
    iconCacheRef.current[key] = { icon, avatarUrl: friend.avatarUrl }
    return icon
  }, [])

  return (
    <MarkerClusterGroup
      chunkedLoading
      iconCreateFunction={createCombinedClusterIcon}
      maxClusterRadius={60}
      disableClusteringAtZoom={16}
      spiderfyOnMaxZoom={true}
      showCoverageOnHover={false}
      removeOutsideVisibleBounds={false}
      animate={false}
      animateAddingMarkers={false}
    >
      {/* Adventure markers */}
      {validAdventures.map(adventure => {
        const isLocked = adventure.meetsJoinCriteria === false
        const requiresSubscription = adventure.requiresSubscription === true
        const hasTestVersion = adventure.hasTestVersion === true
        return (
          <Marker
            key={`adventure-${adventure.id}`}
            position={[adventure.location!.latitude, adventure.location!.longitude]}
            icon={createAdventureMarkerIcon(isLocked, requiresSubscription, adventure.mainCategory, hasTestVersion)}
            // @ts-ignore - custom option for clustering differentiation
            markerType="adventure"
            eventHandlers={onMarkerClick ? {
              click: () => onMarkerClick(adventure)
            } : undefined}
          >
            <Popup maxWidth={300}>
              <div dangerouslySetInnerHTML={{ __html: createPopupHTML(adventure) }} />
            </Popup>
          </Marker>
        )
      })}

      {/* Friend markers */}
      {friends.map((friend, index) => {
        const friendName = friend.userName || 'Friend'
        const icon = getFriendIcon(friend)
        const timeAgo = getTimeAgo(friend.timestamp)

        const popupContent = `
          <div style="display: flex; align-items: center; padding: 4px; min-width: 160px;">
            <div style="
              width: 40px;
              height: 40px;
              border-radius: 50%;
              background: #9C27B0;
              display: flex;
              align-items: center;
              justify-content: center;
              overflow: hidden;
              flex-shrink: 0;
              margin-right: 8px;
            ">
              ${friend.avatarUrl
                ? `<img src="${friend.avatarUrl}" style="width: 100%; height: 100%; object-fit: cover;" />`
                : `<span style="color: white; font-weight: bold; font-size: 14px;">${friendName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}</span>`
              }
            </div>
            <div style="flex: 1;">
              <div style="font-weight: 600; font-size: 13px; margin-bottom: 1px;">${friendName}</div>
              <div style="font-size: 10px; color: #666;">${timeAgo}</div>
            </div>
          </div>
          <div style="display: flex; justify-content: center; gap: 12px; padding: 4px; border-top: 1px solid #eee;">
            <a href="/profile/${friend.userId}" style="
              display: flex;
              align-items: center;
              text-decoration: none;
              color: #666;
              padding: 4px 6px;
              border-radius: 4px;
            " onmouseover="this.style.background='#f5f5f5'" onmouseout="this.style.background='transparent'">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
            </a>
            <a href="/chat?user=${friend.userId}" style="
              display: flex;
              align-items: center;
              text-decoration: none;
              color: #666;
              padding: 4px 6px;
              border-radius: 4px;
            " onmouseover="this.style.background='#f5f5f5'" onmouseout="this.style.background='transparent'">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
              </svg>
            </a>
          </div>
        `

        return (
          <Marker
            key={`friend-${friend.userId || `${friend.latitude}-${friend.longitude}-${index}`}`}
            position={[friend.latitude, friend.longitude]}
            icon={icon}
            zIndexOffset={-500}
            // @ts-ignore - custom option for clustering differentiation
            markerType="friend"
          >
            <Popup>
              <div dangerouslySetInnerHTML={{ __html: popupContent }} />
            </Popup>
          </Marker>
        )
      })}
    </MarkerClusterGroup>
  )
})

// Component for real-time user location marker that updates smoothly
const UserLocationMarker = React.memo(function UserLocationMarker({
  location,
}: {
  location: Location
}) {
  const markerRef = useRef<L.Marker>(null)
  const circleRef = useRef<L.Circle>(null)

  // Update marker position in real-time using refs (avoids React re-render lag)
  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.setLatLng([location.latitude, location.longitude])
    }
    if (circleRef.current && location.accuracy) {
      circleRef.current.setLatLng([location.latitude, location.longitude])
      circleRef.current.setRadius(location.accuracy)
    }
  }, [location.latitude, location.longitude, location.accuracy])

  const icon = location.source === 'gps' ? userLocationIcon : homeLocationIcon

  return (
    <>
      {/* Accuracy circle - only for GPS */}
      {location.source === 'gps' && location.accuracy && (
        <Circle
          ref={circleRef}
          center={[location.latitude, location.longitude]}
          radius={location.accuracy}
          pathOptions={{
            color: '#2196F3',
            fillColor: '#2196F3',
            fillOpacity: 0.1,
            weight: 1,
          }}
        />
      )}

      {/* User location marker - low z-index so it appears behind adventure markers */}
      <Marker
        ref={markerRef}
        position={[location.latitude, location.longitude]}
        icon={icon}
        zIndexOffset={-1000}
      >
        <Popup>
          <Box sx={{ p: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <MyLocation fontSize="small" color={location.source === 'gps' ? 'primary' : 'warning'} />
              <Typography variant="subtitle2">
                {location.source === 'gps' ? 'Your Location' :
                 location.source === 'home' ? 'Home Location' :
                 'Default Location'}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {location.source === 'gps' && location.accuracy
                ? `Accuracy: ±${Math.round(location.accuracy)}m`
                : location.source === 'home'
                ? 'Using saved home location'
                : 'Using default location'}
            </Typography>
          </Box>
        </Popup>
      </Marker>
    </>
  )
})

// Helper function to format time ago (handles UTC timestamps)
function getTimeAgo(timestamp: string): string {
  // Append Z if not present to indicate UTC
  const utcTimestamp = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z'
  const date = new Date(utcTimestamp)
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)

  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  return `${Math.floor(seconds / 86400)}d ago`
}

export function AdventureMap({
  adventures,
  center,
  zoom = 10,
  height = '100%',
  onMarkerClick,
  enableAutoFollow = false,
  onViewportChange,
  showFriendLocations = false,
  friendLocationRefreshInterval = 30000,
}: AdventureMapProps) {
  const { location: userLocation, loading: locationLoading } = useGeolocation()

  // Track if we've set the initial center
  const initialCenterSet = useRef(false)
  const [mapCenter, setMapCenter] = useState<[number, number] | null>(null)

  // Friend locations state
  const [friendLocations, setFriendLocations] = useState<FriendLiveLocationDto[]>([])

  // Fetch friend locations
  useEffect(() => {
    if (!showFriendLocations) {
      setFriendLocations([])
      return
    }

    const fetchFriendLocations = async () => {
      try {
        const locations = await liveLocationApi.getFriendsLiveLocations()
        setFriendLocations(locations)
      } catch (error) {
        console.error('Failed to fetch friend locations:', error)
      }
    }

    // Initial fetch
    fetchFriendLocations()

    // Set up polling interval
    const intervalId = setInterval(fetchFriendLocations, friendLocationRefreshInterval)

    return () => {
      clearInterval(intervalId)
    }
  }, [showFriendLocations, friendLocationRefreshInterval])

  // Set initial center only once when we have location data
  useEffect(() => {
    // Only set center once, ever - bail out if already set
    if (initialCenterSet.current) return

    // If center prop is provided, use it immediately
    if (center) {
      setMapCenter(center)
      initialCenterSet.current = true
      console.log('🗺️ Initial map center set from prop:', center)
      return
    }

    // Wait until location loading is complete
    if (locationLoading) return

    // Now we can set the center based on user location or fallback
    const calculatedCenter = (() => {
      if (userLocation) {
        // First priority: any user location (GPS or home)
        if (userLocation.source === 'gps' || userLocation.source === 'home') {
          return [userLocation.latitude, userLocation.longitude] as [number, number]
        }

        // Second priority: default location if set
        if (userLocation.source === 'default') {
          return [userLocation.latitude, userLocation.longitude] as [number, number]
        }
      }

      // Final fallback
      return [40.7128, -74.0060] as [number, number] // Default to NYC
    })()

    setMapCenter(calculatedCenter)
    initialCenterSet.current = true
    console.log('🗺️ Initial map center set:', calculatedCenter)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLocation, locationLoading, center]) // Reacts until first set

  // If no adventures with location, still show the map with user location only
  // (removed the early return so map always displays)

  // Don't render map until we have the initial center
  if (!mapCenter) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
        <span style={{ color: '#666' }}>Loading map...</span>
      </div>
    )
  }

  return (
    <MapContainer center={mapCenter} zoom={zoom} height={height} zoomControl={true} onViewportChange={onViewportChange}>
      {/* Auto-follow controller */}
      {enableAutoFollow && <MapFollowController userLocation={userLocation} enabled={enableAutoFollow} />}

      {/* User location marker - updates in real-time */}
      {userLocation && <UserLocationMarker location={userLocation} />}

      {/* Combined adventure and friend markers with clustering */}
      <CombinedMarkers
        adventures={adventures}
        friends={showFriendLocations ? friendLocations : []}
        onMarkerClick={onMarkerClick}
      />

      {/* Center on location button */}
      <LocationCenterButton userLocation={userLocation} />
    </MapContainer>
  )
}
